import { Tariff } from '@/lib/types/tariff';
import { Market } from '@addmile/backend-client';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { TariffType } from '../../hooks/useTariffs';
import { useTariffsTable } from './useTariffsTable';

interface TariffsTableProps {
  tariffs: Tariff[];
  onChangeLTV: (tariffId: number, market: Market) => void;
  tariffType: TariffType;
}

const TariffsTable = ({ tariffs, onChangeLTV, tariffType }: TariffsTableProps) => {
  const { data, columns } = useTariffsTable(tariffs, tariffType, onChangeLTV);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 15,
      },
    },
  });

  return (
    <>
      <div className="p-4 bg-white shadow-md rounded-xl bg-white-100 max-w-screen-2xl mt-4 overflow-x-auto">
        <table className="table-auto w-full border-collapse">
          <thead className="bg-gray-100 text-left">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-2 text-base text-black border-b-2 border-primary-20 font-semibold"
                    style={{ minWidth: header.getSize() }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-2 text-base font-medium"
                      style={{ minWidth: cell.column.getSize() }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-center items-center gap-3">
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          className="px-2 py-2 text-base rounded-md disabled:opacity-50 cursor-pointer bg-primary-20"
        >
          <img src={'/icons/arrow-right.svg'} className="rotate-180" />
        </button>
        <span className="text-base font-semibold">
          {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </span>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          className="px-2 py-2 text-base rounded-md disabled:opacity-50 cursor-pointer bg-primary-20"
        >
          <img src={'/icons/arrow-right.svg'} />
        </button>
      </div>
    </>
  );
};

export default TariffsTable;
